// The ready-list home: everything derived from data the app already loads, as pure functions.
//   · appointments  → POST /api/Appointment (useAppointments), bucketed by the Today rules
//   · unsigned notes → GET /api/Patients/GetEncounterListByDraftStatus (useDraftEncounters)
//   · notifications → POST /api/Notifications/UserNotifications (useNotifications)
// Nothing here fetches, and nothing is invented: a value the wire does not carry (a check-in time, a due
// time, a marker target) has no function here and no place on the screen.
import DOMPurify from 'dompurify';
import type { ListAppointmentItem, AppointmentMetaData } from '@/types/appointment-api';
import type { EncounterModel } from '@/types/encounter-api';
import type { NotificationModel } from '@/notifications/types/notification';
import type { AppointmentBuckets } from '@/scheduling/lib/appointment-buckets';
import { glyphKindForAppointment, visitEnded } from '@/scheduling/lib/appointment-glyph';
import { toWorklistCard, type WorklistCardData } from '@/scheduling/lib/to-worklist-card';
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import { formatClockParts, formatClockTime } from '@/shared/lib/time-format';
import type { BucketKey } from '@/scheduling/todays-appointments-view';

/** The strip's default window (minutes of the day): 08:00 to 17:00. It widens by whole hours to hold a visit outside it. */
export const DAY_START_MIN = 8 * 60;
export const DAY_END_MIN = 17 * 60;
/** A free stretch shorter than this is not worth naming. */
export const MIN_FREE_MIN = 15;

const parseMs = (iso: string | undefined | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

const minuteOfDay = (ms: number): number => {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
};

export interface ReadyRow {
  id: string;
  item: ListAppointmentItem;
  bucket: BucketKey;
  card: WorklistCardData;
  glyph: GlyphKind;
  /** The visit type as the wire names it (Follow-up, New patient…). */
  visitType: string | undefined;
  startMs: number | null;
  /** Minutes of the local day; null when the wire time does not parse. */
  startMin: number | null;
  endMin: number | null;
  /** "09:30"; empty when the wire time does not parse. */
  timeLabel: string;
}

/** One row per patient appointment (blocks never come through), earliest first, unparseable times last. */
export function buildReadyRows(
  buckets: AppointmentBuckets,
  metaMap?: ReadonlyMap<string, AppointmentMetaData>,
  nowMs: number = Date.now(),
): ReadyRow[] {
  const rows: ReadyRow[] = [];
  const add = (items: readonly ListAppointmentItem[], bucket: BucketKey) => {
    for (const item of items) {
      const startMs = parseMs(item.start);
      let endMs = parseMs(item.end);
      if (endMs == null && startMs != null && item.minutesDuration) endMs = startMs + item.minutesDuration * 60_000;
      const ended = visitEnded(item, nowMs);
      rows.push({
        id: item.id,
        item,
        bucket,
        card: toWorklistCard(item, { inProgress: bucket === 'in-progress', meta: metaMap?.get(item.id), ended }),
        glyph: glyphKindForAppointment(item.status?.code, bucket, ended),
        visitType: item.appointmentType?.display || undefined,
        startMs,
        startMin: startMs == null ? null : minuteOfDay(startMs),
        endMin: endMs == null || endMs <= (startMs ?? Infinity) ? null : minuteOfDay(endMs),
        timeLabel: startMs == null ? '' : formatClockTime(new Date(startMs)),
      });
    }
  };
  add(buckets.upcoming, 'upcoming');
  add(buckets.inProgress, 'in-progress');
  add(buckets.completed, 'completed');
  return rows.sort((a, b) => (a.startMs ?? Infinity) - (b.startMs ?? Infinity));
}

/** The strip's window in minutes of the day, widened by whole hours so no visit is cut off. */
export function dayWindow(rows: readonly ReadyRow[]): { from: number; to: number } {
  let from = DAY_START_MIN;
  let to = DAY_END_MIN;
  for (const r of rows) {
    if (r.startMin != null) from = Math.min(from, Math.floor(r.startMin / 60) * 60);
    if (r.endMin != null) to = Math.max(to, Math.ceil(r.endMin / 60) * 60);
  }
  return { from, to };
}

/** The rows that can be drawn as a block (both ends known). */
export function timedRows(rows: readonly ReadyRow[]): Array<ReadyRow & { startMin: number; endMin: number }> {
  return rows.filter((r): r is ReadyRow & { startMin: number; endMin: number } => r.startMin != null && r.endMin != null);
}

export interface FreeStretch { from: number; to: number }

/**
 * The free stretches (at least MIN_FREE_MIN) between `nowMin` and the end of the last visit, from the real start and
 * end times. `total` is their sum; the first is the next one.
 */
export function freeStretches(rows: readonly ReadyRow[], nowMin: number, windowFrom = DAY_START_MIN): { stretches: FreeStretch[]; total: number } {
  const stretches: FreeStretch[] = [];
  let cursor = Math.max(nowMin, windowFrom);
  for (const r of timedRows(rows)) {
    if (r.endMin <= cursor) continue;
    if (r.startMin - cursor >= MIN_FREE_MIN) stretches.push({ from: cursor, to: r.startMin });
    cursor = Math.max(cursor, r.endMin);
  }
  return { stretches, total: stretches.reduce((n, s) => n + (s.to - s.from), 0) };
}

/** "2 h 15 min", "45 min". */
export function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "9:30a" / "09:30" from minutes of the day, in the viewer's 12/24-hour format (shared/lib/time-format.ts). */
export function hhmm(min: number): string {
  return formatClockParts(Math.floor(min / 60), min % 60);
}

/** How many visits sit at each stage, by the status glyph the Today screen already uses. */
export function countByGlyph(rows: readonly ReadyRow[]): Record<GlyphKind, number> {
  const counts: Record<GlyphKind, number> = { empty: 0, half: 0, full: 0, done: 0, clock: 0, alert: 0, up: 0, down: 0 };
  for (const r of rows) counts[r.glyph] += 1;
  return counts;
}

export type NeedTone = 'overdue' | 'neutral';

export interface NeedItem {
  /** The appointment id: it opens that encounter. */
  id: string;
  title: string;
  meta: string;
  due: { text: string; tone: NeedTone };
  startMs: number | null;
  /** The patient, when the draft belongs to one of today's visits (the draft itself carries only an id). */
  patientName?: string;
  /** The visit's reason as the draft names it. */
  reason?: string;
  /** The visit's own date and time, long form ("Mon, 12 Oct, 09:00"); undefined when the wire time does not parse. */
  visit?: string;
}

const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * The notes that wait for the practitioner's signature (draft encounters), ranked by urgency: a visit from an earlier
 * day is overdue, oldest first, then today's by start. The patient's name comes from today's appointments when the
 * draft belongs to one; the draft itself carries only the patient id, so an older draft is named by its reason.
 */
export function needsYouItems(
  drafts: readonly EncounterModel[] | undefined,
  nameByAppointment: ReadonlyMap<string, string>,
  nowMs: number,
): NeedItem[] {
  const today = startOfDay(nowMs);
  const items: NeedItem[] = [];
  const seen = new Set<string>();
  for (const e of drafts ?? []) {
    if (!e.appointment || e.isDeleted || seen.has(e.appointment)) continue;
    seen.add(e.appointment);
    const startMs = parseMs(e.startDate);
    const name = nameByAppointment.get(e.appointment);
    const reason = e.reasonForVisitName || e.reasonForVisit || undefined;
    const earlier = startMs != null && startOfDay(startMs) < today;
    const when =
      startMs == null
        ? undefined
        : earlier
          ? new Date(startMs).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
          : formatClockTime(new Date(startMs));
    items.push({
      id: e.appointment,
      title: name ? `Sign note: ${name}` : 'Sign note',
      meta: [reason, when].filter(Boolean).join(' · ') || 'Draft note',
      due: earlier ? { text: 'overdue', tone: 'overdue' } : { text: 'today', tone: 'neutral' },
      startMs,
      patientName: name,
      reason,
      visit:
        startMs == null
          ? undefined
          : `${new Date(startMs).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}, ${formatClockTime(new Date(startMs))}`,
    });
  }
  return items.sort((a, b) => {
    const ao = a.due.tone === 'overdue' ? 0 : 1;
    const bo = b.due.tone === 'overdue' ? 0 : 1;
    return ao - bo || (a.startMs ?? Infinity) - (b.startMs ?? Infinity);
  });
}

/** A notification's message arrives as HTML; the card shows its text. */
export function plainText(html: string | null | undefined): string {
  if (!html) return '';
  // DOMPurify first: it drops a script's body, which a bare textContent would keep as text.
  const safe = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
  const doc = new DOMParser().parseFromString(safe, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export interface InboxRow {
  id: string;
  text: string;
  createdOn: string;
  unread: boolean;
}

/**
 * `limit` notifications, the unread ones first (each loud), then the read ones to fill the card, each group newest first
 * as the wire sends them (`isRead` true is the only read marker).
 */
export function inboxRows(notifications: readonly NotificationModel[], limit = 5): InboxRow[] {
  const rows = notifications.map((n) => ({
    id: n.id,
    text: plainText(n.message) || plainText(n.subject) || 'Notification',
    createdOn: n.createdOn,
    unread: n.isRead !== true,
  }));
  return [...rows.filter((r) => r.unread), ...rows.filter((r) => !r.unread)].slice(0, limit);
}

/** "Dr Haddad" for a physician (the last word of the name), the plain name for anyone else. */
export function homeGreetingName(firstName: string | undefined, lastName: string | undefined, physician: boolean): string {
  const full = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!physician || !full) return full;
  const last = (lastName ?? '').trim().split(/\s+/).at(-1) || full.split(/\s+/).at(-1) || full;
  return `Dr ${last}`;
}
